/**
 * Sistema de diseño UPIM — primitivas únicas reutilizables (REDISEÑO-1/2).
 * Fondo negro, superficies sobrias, acento metálico. Sin neón.
 */
import React, { useState } from "react";
import { cn } from "../../lib/utils";

/**
 * Ruta canónica del activo de identidad autorizado por Jorge.
 * PNG original verificado: SHA-256 d064cf64200a8d1b6f8d89dd19950ea8e9f5dea825e28c91e7486d1393ab9932
 * Instalación reproducible: deploy/rediseno/instalar_logo_oscuro.sh --png <archivo>
 * Los SVG antiguos quedan SOLO como respaldo de rollback y no se muestran.
 */
export const RUTA_MARCA_UPIM = "/assets/upim-logo-web.png";
export const RUTA_MARCA_UPIM_SOBRE_NEGRO = "/assets/upim-logo-dark.webp";
export const RUTA_MARCA_UPIM_TRANSPARENTE = "/assets/upim-logo-dark-transparente.webp";
export const RUTA_ICONO_UPIM = "/assets/upim-marca-icono.webp";

export const LogoUpim = ({
  className = "h-14",
  claseTexto = "text-2xl",
  testId = "upim-logo",
}) => {
  const [sinActivo, setSinActivo] = useState(false);

  if (sinActivo) {
    return (
      <span
        data-testid={`${testId}-tipografico`}
        aria-label="UPIM — Unión de Profesionales Inmobiliarios de México"
        className={cn(
          "font-display font-extrabold leading-none tracking-[0.2em] text-[var(--upim-texto)]",
          claseTexto
        )}
      >
        UPIM
      </span>
    );
  }

  return (
    <img
      src={RUTA_MARCA_UPIM}
      alt="UPIM — Unión de Profesionales Inmobiliarios de México"
      data-testid={testId}
      onError={() => setSinActivo(true)}
      className={cn("w-auto max-w-full shrink-0 bg-transparent object-contain", className)}
      style={{ objectFit: "contain", objectPosition: "center" }}
      draggable={false}
    />
  );
};

export const SeccionUpim = ({ className, children, testId, fondo = "negro", ...rest }) => (
  <section
    data-testid={testId}
    className={cn(
      "relative overflow-hidden",
      fondo === "superficie" && "bg-[var(--upim-superficie-1)]",
      className
    )}
    {...rest}
  >
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">{children}</div>
  </section>
);

export const EncabezadoUpim = ({ etiqueta, titulo, descripcion, className, testId }) => (
  <div data-testid={testId} className={cn("max-w-2xl", className)}>
    {etiqueta && (
      <p className="mb-4 text-xs font-semibold uppercase tracking-[0.22em] text-[var(--upim-acento)]">
        {etiqueta}
      </p>
    )}
    <h2 className="font-display text-3xl font-extrabold leading-tight text-[var(--upim-texto)] md:text-4xl">
      {titulo}
    </h2>
    {descripcion && (
      <p className="mt-4 text-sm leading-relaxed text-[var(--upim-texto-2)] md:text-base">
        {descripcion}
      </p>
    )}
  </div>
);

export const TarjetaUpim = ({ className, children, testId, interactiva = false, ...rest }) => (
  <div
    data-testid={testId}
    className={cn(
      "relative rounded-2xl border border-[var(--upim-borde)] bg-[var(--upim-superficie-2)] p-6",
      interactiva &&
        "transition-[transform,border-color,background-color] duration-300 hover:-translate-y-1 hover:border-[var(--upim-borde-fuerte)] hover:bg-[var(--upim-superficie-3)]",
      className
    )}
    {...rest}
  >
    {children}
  </div>
);

const variantesBoton = {
  solido:
    "bg-[var(--upim-acento)] text-black hover:bg-[var(--upim-acento-2)] border border-transparent",
  contorno:
    "bg-transparent text-[var(--upim-texto)] border border-[var(--upim-borde-fuerte)] hover:border-[var(--upim-acento)] hover:text-[var(--upim-acento)]",
  fantasma:
    "bg-transparent text-[var(--upim-texto-2)] border border-transparent hover:text-[var(--upim-texto)] hover:bg-[var(--upim-superficie-3)]",
  claro: "bg-[var(--upim-texto)] text-black border border-transparent hover:bg-white",
};

export const BotonUpim = React.forwardRef(
  ({ variante = "solido", className, children, testId, as: Etiqueta = "button", ...rest }, ref) => (
    <Etiqueta
      ref={ref}
      data-testid={testId}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold tracking-tight transition-[background-color,color,border-color,transform] duration-200 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-45",
        variantesBoton[variante],
        className
      )}
      {...rest}
    >
      {children}
    </Etiqueta>
  )
);
BotonUpim.displayName = "BotonUpim";

export const EstadoVacioUpim = ({ icono: Icono, titulo, descripcion, accion, testId = "estado-vacio" }) => (
  <div
    data-testid={testId}
    className="flex flex-col items-start gap-3 rounded-2xl border border-dashed border-[var(--upim-borde-fuerte)] bg-[var(--upim-superficie-1)] px-6 py-12"
  >
    {Icono && (
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--upim-acento-suave)] text-[var(--upim-acento)]">
        <Icono className="h-5 w-5" aria-hidden="true" />
      </span>
    )}
    <p className="font-display text-lg font-bold text-[var(--upim-texto)]">{titulo}</p>
    {descripcion && (
      <p className="max-w-md text-sm text-[var(--upim-texto-2)]">{descripcion}</p>
    )}
    {accion}
  </div>
);

export const EtiquetaUpim = ({ children, tono = "neutro", className, testId }) => (
  <span
    data-testid={testId}
    className={cn(
      "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
      tono === "acento"
        ? "border-[rgba(200,168,75,0.35)] bg-[var(--upim-acento-suave)] text-[var(--upim-acento)]"
        : "border-[var(--upim-borde)] bg-[var(--upim-superficie-2)] text-[var(--upim-texto-2)]",
      className
    )}
  >
    {children}
  </span>
);
